import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getLocations, getCategories, getRepairShops } from '../lib/api';
import { useAuth } from './AuthContext';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { user, isAdminOrOps } = useAuth();
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [repairShops, setRepairShops] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      const [locRes, catRes, shopRes] = await Promise.all([
        getLocations(),
        getCategories(),
        getRepairShops(),
      ]);
      const activeLocations = locRes.data.filter(l => l.is_active);
      setLocations(activeLocations);
      setCategories(catRes.data.filter(c => c.is_active));
      setRepairShops(shopRes.data.filter(s => s.is_active));
      
      // Set default location based on user role
      if (isAdminOrOps()) {
        setSelectedLocation(activeLocations[0]?.id || null);
      } else if (user.assigned_location_ids?.length > 0) {
        setSelectedLocation(user.assigned_location_ids[0]);
      }
    } catch (error) {
      console.error('Failed to load app data:', error);
    } finally {
      setLoading(false);
    }
  }, [user, isAdminOrOps]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, loadData]);

  const refreshData = () => loadData();

  const getLocationName = (id) => {
    return locations.find(l => l.id === id)?.name || 'Unknown';
  };

  const getCategoryName = (id) => {
    return categories.find(c => c.id === id)?.name || 'Unknown';
  };

  const getRepairShopName = (id) => {
    return repairShops.find(s => s.id === id)?.name || 'Unknown';
  };

  // Get locations user can access
  const getAccessibleLocations = () => {
    if (isAdminOrOps()) {
      return locations;
    }
    return locations.filter(loc => 
      (user?.assigned_location_ids || []).includes(loc.id)
    );
  };

  return (
    <AppContext.Provider value={{
      locations,
      categories,
      repairShops,
      selectedLocation,
      setSelectedLocation,
      loading,
      refreshData,
      getLocationName,
      getCategoryName,
      getRepairShopName,
      getAccessibleLocations,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
